'use es6';

import tinymce from 'tinymce';
import { __HS_LISTS } from '../constants/plugins';
import { getListNode } from '../utils/formatting';
import { isNestedFirstChild, isFirstChild, isNestedLastChild } from '../hsformatting/utils';
import { BOGUS_BR } from '../utils/dom';
import { getParentCallout } from '../utils/callout';
const DELETE_KEY_CODE = 8;
const ENTER_KEY_CODE = 13;
const blockNodes = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'PRE', 'BR'];

/*
Upon delete from the start of an <li>, delete up into the previous <li>
but on a newline and retains the indent:
  1. alpha
  2. |beta
  3. gamma
---- 'DELETE' ---->
  1. alpha
     beta
  2. gamma
*/
const deleteIntoPrevLi = (editor, listNode, evt) => {
  const prevListNode = listNode.previousSibling;
  if (!prevListNode) return;
  editor.undoManager.transact(() => {
    const content = listNode.innerHTML;
    editor.dom.remove(listNode);
    editor.selection.setCursorLocation(prevListNode, prevListNode.childNodes.length);
    if (prevListNode.lastChild && !blockNodes.includes(prevListNode.lastChild.nodeName)) {
      editor.selection.setContent('<br>');
    }
    const bm = editor.selection.getBookmark();
    editor.selection.setContent(content);
    editor.selection.moveToBookmark(bm);
  });
  evt.preventDefault();
  evt.stopImmediatePropagation();
};

/*
Upon deleting from an empty <li> and then again from the indented trailing <br>
in the previous <li>, escape and/or split the larger list block:
  1. alpha
     |
  2. beta
---- 'DELETE' ---->
  1. alpha
  <p>|</p>
  1. beta
[-----------------]
  1. alpha
      1. beta
         |
  2. gamma
---- 'DELETE' ---->
  1. alpha
      1. beta
  <p>|</p>
  1. gamma
*/
const escapeListBlock = (editor, rng, evt) => {
  const container = rng.startContainer;
  let node = container.childNodes[rng.startOffset];
  if (node.nodeName !== 'BR') return;
  const parentListBlocks = editor.dom.getParents(container, 'ul,ol', editor.getBody());
  if (!parentListBlocks) return;
  const outerParentBlock = parentListBlocks[parentListBlocks.length - 1];
  const newBlock = editor.dom.create(editor.settings.forced_root_block || 'p', {}, BOGUS_BR);
  editor.undoManager.transact(() => {
    if (!isNestedLastChild(container, outerParentBlock)) {
      const tmpRng = rng.cloneRange();
      const parentLiNodes = editor.dom.getParents(container, 'li', outerParentBlock);
      let nextSibling;
      parentLiNodes.forEach(liNode => {
        if (nextSibling) return;
        nextSibling = liNode.nextSibling;
      });
      tmpRng.setStartBefore(nextSibling);
      tmpRng.setEndAfter(outerParentBlock);
      const fragment = tmpRng.extractContents();

      // Start of list fragment is a nested <li>, don't show list styles
      let firstLiNode = fragment.firstChild.firstChild;
      let hasListBlockAsChild = firstLiNode.childNodes.length === 1 && ['UL', 'OL'].includes(firstLiNode.firstChild.nodeName);
      while (hasListBlockAsChild) {
        firstLiNode.setAttribute('style', 'list-style-type: none;');
        firstLiNode = firstLiNode.firstChild.firstChild;
        hasListBlockAsChild = firstLiNode && firstLiNode.childNodes.length === 1 && ['UL', 'OL'].includes(firstLiNode.firstChild.nodeName);
      }
      editor.dom.insertAfter(fragment, outerParentBlock);
    }
    if (isFirstChild(container) && container.childNodes.length === 1) {
      node = container;
    }
    editor.dom.remove(node);
    editor.dom.insertAfter(newBlock, outerParentBlock);
    if (!outerParentBlock.hasChildNodes()) {
      editor.dom.remove(outerParentBlock);
    }
    editor.selection.setCursorLocation(newBlock, 0);
  });
  evt.stopImmediatePropagation();
  evt.preventDefault();
};
const removeListFormatFromCallout = (editor, listNode, evt) => {
  editor.undoManager.transact(() => {
    let replacement;
    const bookmark = editor.selection.getBookmark();
    if (listNode.childNodes.length > 1) {
      replacement = editor.dom.create('p', {}, listNode.innerHTML);
    } else {
      if (listNode.firstChild.nodeName !== 'P') {
        replacement = editor.dom.create('p', {}, listNode.innerHTML);
      } else {
        replacement = listNode.firstChild.cloneNode(true);
      }
    }
    listNode.innerHTML = '';
    listNode.appendChild(replacement);
    const listType = editor.dom.getParent(listNode, 'ul,ol').nodeName;
    const cmd = listType === 'UL' ? 'InsertUnorderedList' : 'InsertOrderedList';
    editor.execCommand(cmd);
    const replacementNode = editor.dom.get(`${bookmark.id}_start`).parentNode;
    if (replacementNode.nextSibling && replacementNode.nextSibling.nodeName === 'BR') {
      editor.dom.remove(replacementNode.nextSibling);
    }
    editor.selection.moveToBookmark(bookmark);
  });
  evt.stopImmediatePropagation();
  evt.preventDefault();
};
function hslistsPlugin(editor) {
  editor.on('KeyUp', evt => {
    if (evt.keyCode !== ENTER_KEY_CODE || !editor.selection.isCollapsed()) {
      return;
    }
    const selectedListNode = getListNode(editor, editor.selection.getNode());
    if (!selectedListNode) return;
    const prevListNode = selectedListNode.previousSibling;
    if (prevListNode && prevListNode.innerHTML === BOGUS_BR) {
      prevListNode.innerHTML = '<br>';
    }
    if (selectedListNode.innerHTML === BOGUS_BR) {
      selectedListNode.innerHTML = '<br>';
    }
  }, true);
  editor.on('KeyDown', evt => {
    if (evt.keyCode !== DELETE_KEY_CODE || !editor.selection.isCollapsed()) return;
    const selectedListNode = getListNode(editor, editor.selection.getNode());
    if (!selectedListNode) return;
    const rng = editor.selection.getRng();
    const container = rng.startContainer;
    if (rng.startOffset === 0 && isNestedFirstChild(container, selectedListNode)) {
      const parentCallout = getParentCallout(editor, selectedListNode);
      const cursorIsAtBeginningOfCallout = parentCallout && isNestedFirstChild(container, parentCallout);
      if (cursorIsAtBeginningOfCallout) {
        removeListFormatFromCallout(editor, selectedListNode, evt);
      } else {
        deleteIntoPrevLi(editor, selectedListNode, evt);
      }
    } else if (selectedListNode === container && container.hasChildNodes() && rng.startOffset === container.childNodes.length - 1) {
      escapeListBlock(editor, rng, evt);
    }
  }, true);
}
export const HS_LISTS = __HS_LISTS;
tinymce.PluginManager.add(HS_LISTS, hslistsPlugin);